/**
 * Point label for championship and tournament matches.
 *
 * Human reference: docs/point-label.md
 * Keep the ids in sync with Backend/src/utils/matchPointLabel.pure.js.
 *
 * Users set `outcome` only. `phase`, `stroke`, `placement`, and `context`
 * stay empty until video analysis fills them.
 */

export const POINT_OUTCOMES = [
  {
    id: "winner",
    mark: "🟢",
    labelKey: "cmOutcomeWinner",
    label: "Winner",
    titleKey: "cmOutcomeWinnerTitle",
    title: "Winner",
  },
  {
    id: "unforced_error",
    mark: "🔴",
    labelKey: "cmOutcomeError",
    label: "Error",
    titleKey: "cmOutcomeErrorTitle",
    title: "Unforced error",
  },
  {
    id: "forced_error",
    mark: "🟡",
    labelKey: "cmOutcomeForced",
    label: "Forced",
    titleKey: "cmOutcomeForcedTitle",
    title: "Forced error",
  },
  {
    id: "lucky",
    mark: "⚪",
    labelKey: "cmOutcomeLucky",
    label: "Lucky",
    titleKey: "cmOutcomeLuckyTitle",
    title: "Lucky, net, or edge",
  },
  {
    id: "rally",
    mark: "🔵",
    labelKey: "cmOutcomeRally",
    label: "Rally",
    titleKey: "cmOutcomeRallyTitle",
    title: "Rally",
  },
];

export const POINT_OUTCOME_IDS = POINT_OUTCOMES.map((row) => row.id);

/** Older tag ids that mean the same outcome. */
export const LEGACY_OUTCOME_TAGS = {
  winner: "winner",
  winner_open_play: "winner",
  return_winner: "winner",
  unforced_error: "unforced_error",
  return_error: "unforced_error",
  forced_error: "forced_error",
  lucky: "lucky",
  net: "lucky",
  edge: "lucky",
  rally: "rally",
};

export const POINT_PHASES = ["serve", "receive", "rally", "finish"];

export const POINT_STROKES = [
  "forehand",
  "backhand",
  "push",
  "topspin",
  "block",
  "flip",
  "smash",
];

export const POINT_PLACEMENTS = ["short", "long", "wide_fh", "wide_bh", "elbow"];

export const POINT_CONTEXTS = [
  "important_point",
  "comeback",
  "streak",
  "set_point",
];

/**
 * Stored on tbl_championship_point.label.
 * outcome is user-set. The other fields are for video analysis.
 */
export function emptyPointLabel() {
  return {
    outcome: null,
    phase: null,
    stroke: null,
    placement: null,
    context: [],
  };
}

function oneOf(value, allowed) {
  const key = String(value || "").trim();
  return allowed.includes(key) ? key : null;
}

export function normalizePointLabel(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const context = [];
  const seen = new Set();
  for (const item of Array.isArray(src.context) ? src.context : []) {
    const key = oneOf(item, POINT_CONTEXTS);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    context.push(key);
  }
  return {
    outcome: oneOf(src.outcome, POINT_OUTCOME_IDS),
    phase: oneOf(src.phase, POINT_PHASES),
    stroke: oneOf(src.stroke, POINT_STROKES),
    placement: oneOf(src.placement, POINT_PLACEMENTS),
    context,
  };
}

export function isOutcomeTag(tag) {
  return Object.prototype.hasOwnProperty.call(LEGACY_OUTCOME_TAGS, String(tag || ""));
}

/** Prefer label.outcome. Otherwise the first matching tag, in outcome order. */
export function readPointOutcome(point) {
  const fromLabel = normalizePointLabel(point?.label).outcome;
  if (fromLabel) return fromLabel;
  const tags = Array.isArray(point?.tags) ? point.tags : [];
  for (const id of POINT_OUTCOME_IDS) {
    if (tags.some((tag) => LEGACY_OUTCOME_TAGS[tag] === id)) return id;
  }
  return null;
}

export function outcomeMeta(outcomeId) {
  return POINT_OUTCOMES.find((row) => row.id === outcomeId) || null;
}

/** Drop every outcome alias, then store at most one outcome id. */
export function applyOutcomeTags(tags, outcomeId) {
  const next = (Array.isArray(tags) ? tags : []).filter((tag) => !isOutcomeTag(tag));
  const outcome = oneOf(outcomeId, POINT_OUTCOME_IDS);
  if (outcome) next.push(outcome);
  return next;
}

export function pointLabelWithOutcome(existing, outcomeId) {
  return {
    ...normalizePointLabel(existing),
    outcome: oneOf(outcomeId, POINT_OUTCOME_IDS),
  };
}

/** Selecting the active outcome clears it. Any other selection replaces it. */
export function togglePointOutcome(point, outcomeId) {
  const current = readPointOutcome(point);
  const next = current === outcomeId ? null : oneOf(outcomeId, POINT_OUTCOME_IDS);
  return {
    tags: applyOutcomeTags(point?.tags, next),
    label: pointLabelWithOutcome(point?.label, next),
  };
}

export function pointMatchesOutcome(point, outcomeId) {
  if (!outcomeId) return true;
  if (POINT_OUTCOME_IDS.includes(outcomeId)) {
    return readPointOutcome(point) === outcomeId;
  }
  return (point?.tags || []).includes(outcomeId);
}
